import { useCallback, useEffect, useRef, useState } from "react";
import { Clipboard, ClipboardPaste, Loader2, Power, RotateCcw, TerminalSquare } from "lucide-react";
import { FitAddon } from "@xterm/addon-fit";
import { WebLinksAddon } from "@xterm/addon-web-links";
import { Terminal } from "@xterm/xterm";
import "@xterm/xterm/css/xterm.css";

import { apiRequest, APIError } from "@/lib/api";

type SSHMetadata = {
  job_id: string;
  status: string;
  host: string;
  port: number;
  username: string;
  command: string;
  host_key_fingerprint: string;
  terminal_enabled: boolean;
};

type TerminalTicket = {
  session_id: string;
  ticket: string;
  expires_at: string;
  ws_path: string;
  protocol: string;
};

type TerminalPhase =
  | "loading"
  | "ready"
  | "connecting"
  | "connected"
  | "reconnecting"
  | "disconnected"
  | "error";

const encoder = new TextEncoder();

function errorMessage(error: unknown) {
  if (error instanceof APIError) return error.detail;
  if (error instanceof Error) return error.message;
  return "The SSH terminal could not be opened.";
}

export function WebSSHTerminal({ jobId }: { jobId: string }) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const terminalRef = useRef<Terminal | null>(null);
  const fitRef = useRef<FitAddon | null>(null);
  const socketRef = useRef<WebSocket | null>(null);
  const sessionIDRef = useRef("");
  const reconnectTimerRef = useRef<number | null>(null);
  const reconnectAttemptRef = useRef(0);
  const autoConnectedJobRef = useRef("");
  const intentionalCloseRef = useRef(false);
  const mountedRef = useRef(true);
  const [metadata, setMetadata] = useState<SSHMetadata | null>(null);
  const [phase, setPhase] = useState<TerminalPhase>("loading");
  const [message, setMessage] = useState("Checking secure SSH access…");

  const clearReconnectTimer = useCallback(() => {
    if (reconnectTimerRef.current !== null) {
      window.clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
  }, []);

  const sendResize = useCallback(() => {
    const socket = socketRef.current;
    const terminal = terminalRef.current;
    if (!socket || socket.readyState !== WebSocket.OPEN || !terminal) return;
    socket.send(JSON.stringify({ type: "resize", cols: terminal.cols, rows: terminal.rows }));
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    const terminal = new Terminal({
      cursorBlink: true,
      convertEol: false,
      fontFamily: '"SFMono-Regular", Consolas, "Liberation Mono", monospace',
      fontSize: 13,
      lineHeight: 1.2,
      scrollback: 10_000,
      theme: {
        background: "#080b10",
        foreground: "#e6edf3",
        cursor: "#7dd3fc",
        selectionBackground: "#1d4ed880",
        black: "#111827",
        brightBlack: "#64748b",
      },
    });
    const fit = new FitAddon();
    terminal.loadAddon(fit);
    terminal.loadAddon(new WebLinksAddon());
    terminal.open(containerRef.current!);
    fit.fit();
    terminalRef.current = terminal;
    fitRef.current = fit;
    terminal.writeln("\x1b[90mGENRL secure web SSH terminal\x1b[0m");

    const input = terminal.onData((data) => {
      const socket = socketRef.current;
      if (socket?.readyState === WebSocket.OPEN) socket.send(encoder.encode(data));
    });
    const resizeObserver = new ResizeObserver(() => {
      fit.fit();
      sendResize();
    });
    resizeObserver.observe(containerRef.current!);

    return () => {
      mountedRef.current = false;
      clearReconnectTimer();
      resizeObserver.disconnect();
      input.dispose();
      socketRef.current?.close(1000, "terminal view closed");
      socketRef.current = null;
      terminal.dispose();
      terminalRef.current = null;
      fitRef.current = null;
    };
  }, [clearReconnectTimer, sendResize]);

  useEffect(() => {
    const controller = new AbortController();
    let retryTimer: number | null = null;
    autoConnectedJobRef.current = "";
    setPhase("loading");
    setMessage("Checking secure SSH access…");

    const loadMetadata = async () => {
      try {
        const payload = await apiRequest<SSHMetadata>(
          `/api/v1/provider-jobs/${encodeURIComponent(jobId)}/ssh`,
          { signal: controller.signal, cache: "no-store" },
        );
        if (!mountedRef.current) return;
        setMetadata(payload);
        setPhase("ready");
        setMessage("SSH is verified. Opening terminal…");
      } catch (error) {
        if (!mountedRef.current || controller.signal.aborted) return;
        const retryable =
          error instanceof APIError &&
          ["ERR_TERMINAL_NOT_READY", "ERR_TERMINAL_JOB_NOT_FOUND"].includes(error.code);
        if (retryable) {
          setPhase("loading");
          setMessage("VM is provisioning. Waiting for verified SSH access…");
          retryTimer = window.setTimeout(() => void loadMetadata(), 5_000);
          return;
        }
        setPhase("error");
        setMessage(errorMessage(error));
      }
    };

    void loadMetadata();
    return () => {
      controller.abort();
      if (retryTimer !== null) window.clearTimeout(retryTimer);
    };
  }, [jobId]);

  const openSocket = useCallback(
    (ticket: TerminalTicket, reconnecting: boolean) => {
      clearReconnectTimer();
      setPhase(reconnecting ? "reconnecting" : "connecting");
      setMessage(
        reconnecting ? "Reconnecting to the existing shell…" : "Opening encrypted SSH session…",
      );
      const socket = new WebSocket(ticket.ws_path, [ticket.protocol, `ticket.${ticket.ticket}`]);
      socket.binaryType = "arraybuffer";
      socketRef.current = socket;
      socket.onopen = () => {
        if (!mountedRef.current) return;
        reconnectAttemptRef.current = 0;
        setPhase("connected");
        setMessage("Connected");
        fitRef.current?.fit();
        sendResize();
        terminalRef.current?.focus();
      };
      socket.onmessage = (event) => {
        if (event.data instanceof ArrayBuffer) {
          terminalRef.current?.write(new Uint8Array(event.data));
        } else if (event.data instanceof Blob) {
          void event.data
            .arrayBuffer()
            .then((buffer) => terminalRef.current?.write(new Uint8Array(buffer)));
        }
      };
      socket.onerror = () => {
        if (mountedRef.current) setMessage("The terminal connection was interrupted.");
      };
      socket.onclose = (event) => {
        if (socketRef.current === socket) socketRef.current = null;
        if (!mountedRef.current || intentionalCloseRef.current) return;
        if (event.code === 1008 || event.code === 1003) {
          setPhase("error");
          setMessage(event.reason || "The terminal session expired.");
          return;
        }
        const attempt = reconnectAttemptRef.current + 1;
        reconnectAttemptRef.current = attempt;
        if (attempt > 8 || !sessionIDRef.current) {
          setPhase("disconnected");
          setMessage("Disconnected. Use reconnect to resume the shell.");
          return;
        }
        setPhase("reconnecting");
        setMessage(`Connection lost. Reconnecting (${attempt}/8)…`);
        reconnectTimerRef.current = window.setTimeout(
          async () => {
            try {
              const refreshed = await apiRequest<TerminalTicket>(
                `/api/v1/terminal-sessions/${encodeURIComponent(sessionIDRef.current)}/ticket`,
                { method: "POST", cache: "no-store" },
              );
              if (mountedRef.current && !intentionalCloseRef.current) openSocket(refreshed, true);
            } catch (error) {
              if (!mountedRef.current) return;
              setPhase("error");
              setMessage(errorMessage(error));
            }
          },
          Math.min(1_000 * 2 ** (attempt - 1), 15_000),
        );
      };
    },
    [clearReconnectTimer, sendResize],
  );

  const connect = useCallback(async () => {
    intentionalCloseRef.current = false;
    clearReconnectTimer();
    try {
      let ticket: TerminalTicket;
      if (sessionIDRef.current) {
        ticket = await apiRequest<TerminalTicket>(
          `/api/v1/terminal-sessions/${encodeURIComponent(sessionIDRef.current)}/ticket`,
          { method: "POST", cache: "no-store" },
        );
      } else {
        ticket = await apiRequest<TerminalTicket>(
          `/api/v1/provider-jobs/${encodeURIComponent(jobId)}/terminal-sessions`,
          { method: "POST", cache: "no-store" },
        );
        sessionIDRef.current = ticket.session_id;
      }
      openSocket(ticket, reconnectAttemptRef.current > 0);
    } catch (error) {
      setPhase("error");
      setMessage(errorMessage(error));
    }
  }, [clearReconnectTimer, jobId, openSocket]);

  useEffect(() => {
    if (!metadata || phase !== "ready" || autoConnectedJobRef.current === jobId) return;
    autoConnectedJobRef.current = jobId;
    void connect();
  }, [connect, jobId, metadata, phase]);

  const closeSession = useCallback(async () => {
    intentionalCloseRef.current = true;
    clearReconnectTimer();
    socketRef.current?.close(1000, "user closed terminal");
    socketRef.current = null;
    const sessionID = sessionIDRef.current;
    sessionIDRef.current = "";
    if (sessionID) {
      try {
        await apiRequest(`/api/v1/terminal-sessions/${encodeURIComponent(sessionID)}`, {
          method: "DELETE",
          cache: "no-store",
        });
      } catch {
        // The socket is already closed; the server's session TTL remains the fallback cleanup.
      }
    }
    terminalRef.current?.writeln("\r\n\x1b[90mTerminal session closed.\x1b[0m");
    setPhase("ready");
    setMessage("SSH is verified. Connect when you are ready.");
    intentionalCloseRef.current = false;
  }, [clearReconnectTimer]);

  const copySelection = useCallback(async () => {
    const selection = terminalRef.current?.getSelection() || "";
    if (selection) await navigator.clipboard.writeText(selection);
  }, []);

  const pasteClipboard = useCallback(async () => {
    const value = await navigator.clipboard.readText();
    if (value) terminalRef.current?.paste(value);
  }, []);

  const busy = phase === "loading" || phase === "connecting" || phase === "reconnecting";
  const connected = phase === "connected";

  return (
    <section className="flex min-h-[430px] flex-col overflow-hidden border border-[var(--wallet-ops-border)] bg-[#080b10]">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-800 bg-slate-950 px-3 py-2 text-slate-200">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-xs font-semibold">
            <span
              className={`h-2 w-2 rounded-full ${connected ? "bg-emerald-400" : busy ? "animate-pulse bg-amber-300" : "bg-slate-500"}`}
            />
            <span>{message}</span>
          </div>
          {metadata ? (
            <p className="mt-1 truncate font-mono text-[10px] text-slate-500">
              {metadata.username}@{metadata.host}:{metadata.port} · {metadata.host_key_fingerprint}
            </p>
          ) : null}
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            className="wallet-ops-button !min-h-0 px-2 py-1.5 text-xs"
            onClick={() => void copySelection()}
            disabled={!connected}
            aria-label="Copy terminal selection"
          >
            <Clipboard className="h-3.5 w-3.5" /> Copy
          </button>
          <button
            type="button"
            className="wallet-ops-button !min-h-0 px-2 py-1.5 text-xs"
            onClick={() => void pasteClipboard()}
            disabled={!connected}
            aria-label="Paste into terminal"
          >
            <ClipboardPaste className="h-3.5 w-3.5" /> Paste
          </button>
          {connected || phase === "reconnecting" ? (
            <button
              type="button"
              className="wallet-ops-button !min-h-0 px-2 py-1.5 text-xs"
              onClick={() => void closeSession()}
              aria-label="Close terminal session"
            >
              <Power className="h-3.5 w-3.5" /> Close
            </button>
          ) : (
            <button
              type="button"
              className="wallet-ops-button !min-h-0 px-2 py-1.5 text-xs"
              onClick={() => void connect()}
              disabled={busy || !metadata}
              aria-label="Connect SSH terminal"
            >
              {busy ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : sessionIDRef.current ? (
                <RotateCcw className="h-3.5 w-3.5" />
              ) : (
                <TerminalSquare className="h-3.5 w-3.5" />
              )}
              {sessionIDRef.current ? "Reconnect" : "Connect"}
            </button>
          )}
        </div>
      </div>
      <div
        ref={containerRef}
        className="min-h-0 flex-1 p-2"
        role="application"
        aria-label="SSH terminal"
        onPointerDown={() => terminalRef.current?.focus()}
      />
      <p className="border-t border-slate-800 bg-slate-950 px-3 py-2 text-[10px] text-slate-500">
        This is an interactive shell on the selected remote machine. Commands run with that
        machine&apos;s SSH user permissions. Credentials remain on the GENRL backend.
      </p>
    </section>
  );
}
